import { Box, Grid } from "@mui/material";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useRoles } from "api/roles";
import { useStores } from "api/stores";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useState } from "react";

import { hasModifyAccessToResource } from "api/general";
import { EmptyState } from "components/EmptyState/EmptyState";
import { FormButtons } from "components/FormControls/FormButtons";
import { ReactComponent as KeyIcon } from "components/Icons/Security-Key-01.svg";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { OLButton } from "OLButton";
import { useNavigate } from "react-router-dom";
import {
  Access,
  Permission,
  PermissionCodes,
  RoleBackendModel,
} from "types/role";
import { alphabetizeByProperty } from "utilities/arrays";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { Permissions } from "./Permissions";
import { useStyles } from "./RoleForm.styles";

export function RoleForm(props: {
  editMode?: boolean;
  initialValues?: RoleBackendModel;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { addRole, editRole } = useRoles();
  const { useInfiniteStoreList, useStoreDetails } = useStores();
  const initialStores = useStoreDetails(props?.initialValues?.stores || []);

  const [nameSearch, setNameSearch] = useState("");

  const {
    data: infiniteStoresList,
    size: storesSize,
    setSize: setStoresSize,
    isValidating,
  } = useInfiniteStoreList({
    filters: nameSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: nameSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    rows: 50,
    sortKey: "name",
  });

  const { infiniteData: storesList, anyElementsToFetch: anyStoresToFetch } =
    useManageInfiniteScroll(infiniteStoresList || []);

  const filteredStoreOptions = storesList || [];
  const isRoleMaster = props?.initialValues?.master;

  const initialValues = props.initialValues
    ? {
        ...props.initialValues,
        permissions: props?.initialValues?.permissions?.map(
          ({ access, resource }: Permission) => ({
            access,
            resource,
          }),
        ),
      }
    : {
        default: false,
        id: "",
        master: false,
        name: "",
        permissions: [
          { resource: PermissionCodes.STORES, access: Access.VIEW },
        ],
        role: "",
        stores: [],
      };

  const hasModifyRolePermission = hasModifyAccessToResource("ACL");
  const isFormDataReady =
    (props.editMode && props.initialValues) || !props.editMode;

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});
        const properties = {
          ...(({ master, id, role, ...rest }) => rest)(values),
        };
        try {
          const response =
            props.editMode && props.initialValues
              ? await editRole(props.initialValues.id, properties)
              : await addRole(properties);
          if (response) {
            navigate(links.settings);
            notify(
              message({ id: props.editMode ? "roleEdited" : "roleAdded" }),
            );
          }
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          setStatus(errors);
          focusError();
        }
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        status,
        values,
        dirty,
        initialValues,
      }) => {
        return (
          <Form
            className={!hasModifyRolePermission ? classes.disabled : ""}
            onSubmit={handleSubmit}
          >
            <FormSection
              isLoading={!isFormDataReady}
              sectionError={status?.[""]}
              title={message({ id: "basicSettings" })}
            >
              <Grid container spacing={12}>
                <Grid item xs={6}>
                  <TextInput
                    className={classes.SelectInput}
                    data-test-id="name"
                    disabled={!hasModifyRolePermission}
                    label={
                      <RequiredFieldLabel text={message({ id: "name" })} />
                    }
                    name="name"
                  />
                </Grid>
                <Grid item xs={6}>
                  <SelectInput
                    asyncSearch={(value) => setNameSearch(value)}
                    className={classes.SelectInput}
                    data-test-id="add-tenant"
                    disabled={!hasModifyRolePermission}
                    displaySearch
                    initialValue={initialStores.map(({ code, name }) => ({
                      id: code,
                      label: name,
                    }))}
                    items={alphabetizeByProperty(
                      filteredStoreOptions.map(({ code, name }) => ({
                        label: name,
                        id: code,
                      })),
                      "label",
                    )}
                    label={message({ id: "addTenant" })}
                    multiple
                    name="stores"
                    placeholder={message({ id: "addTenant" })}
                    loadMoreElements={() =>
                      !isValidating && setStoresSize(storesSize + 1)
                    }
                    anyElementsToFetch={anyStoresToFetch}
                    helperText={message({ id: "manageRoleTenantInfo" })}
                  />
                </Grid>
              </Grid>
            </FormSection>
            {isRoleMaster ? (
              <FormSection
                isLoading={!isFormDataReady}
                title={message({ id: "permissions" })}
              >
                <EmptyState
                  button={
                    <OLButton disabled variant="contained" size="medium">
                      {message({ id: "addPermission" })}
                    </OLButton>
                  }
                  icon={<KeyIcon />}
                  title={message({ id: "allPossiblePermissions" })}
                />
              </FormSection>
            ) : (
              <Permissions isFormDataReady={!!isFormDataReady} />
            )}
            <FormSection
              isLoading={!isFormDataReady}
              title={message({ id: "defaultRole" })}
            >
              <Grid container spacing={12}>
                <Grid item xs={12}>
                  <Toggle
                    data-test-id="status"
                    disabled={!hasModifyRolePermission}
                    helperText={message({ id: "defaultRoleDescription" })}
                    label={message({ id: "default" })}
                    name="default"
                  />
                </Grid>
              </Grid>
            </FormSection>
            <Box mt={12} />
            <FormButtons
              disableSubmit={isSubmitting || !hasModifyRolePermission}
              onCancelClick={() => navigate(links.settings)}
              onSubmitClick={() => handleSubmit()}
              {...(!props.editMode && { submitMessage: "addRole" })}
            />
            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
